import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Storage as StorageIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Save as SaveIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  SettingsBackupRestore as RestoreIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  Restore as RestoreHistoryIcon,
} from '@mui/icons-material';
import unitsBackupService from '../../../services/unitsBackupService';
import Select from '../../../components/Select';
import MultiSelect from '../../../components/MultiSelect';
import DateRangePicker from '../../../components/DateRangePicker';
import TimePicker from '../../../components/TimePicker';
import Table from '../../../components/Table';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import {
  fetchInitRequest,
  selectUnits,
  selectTypes,
  selectStatuses,
  selectMarks,
  fetchBackupConfigRequest,
  selectBackupConfig,
  clearBackupConfig,
  saveBackupConfigRequest,
  selectSaveLoading,
  selectSaveSuccess,
  selectSaveSuccessMessage,
  selectSaveError,
  clearSaveStatus,
  manualBackupRequest,
  selectManualBackupLoading,
  selectManualBackupSuccess,
  selectManualBackupSuccessMessage,
  selectManualBackupError,
  clearManualBackupStatus,
  fetchBackupLogsRequest,
  selectBackupLogs,
  selectBackupLogsLoading,
  selectBackupLogsTotal,
  selectBackupLogsPage,
  selectBackupLogsSize,
  setBackupLogsPage,
  setBackupLogsSize,
  selectFilterUnitIds,
  selectFilterTypes,
  selectFilterStatuses,
  selectFilterIsMarks,
  selectFilterFromDate,
  selectFilterToDate,
  setFilterUnitIds,
  setFilterTypes,
  setFilterStatuses,
  setFilterIsMarks,
  setFilterFromDate,
  setFilterToDate,
  markBackupLogRequest,
  selectMarkLoading,
  selectMarkSuccess,
  selectMarkSuccessMessage,
  selectMarkError,
  clearMarkStatus,
  restoreBackupLogRequest,
  selectRestoreLoading,
  selectRestoreSuccess,
  selectRestoreSuccessMessage,
  selectRestoreError,
  clearRestoreStatus,
  // Restore logs (Tab 3)
  fetchRestoreLogsRequest,
  selectRestoreLogs,
  selectRestoreLogsLoading,
  selectRestoreLogsTotal,
  selectRestoreLogsPage,
  selectRestoreLogsSize,
  setRestoreLogsPage,
  setRestoreLogsSize,
  selectFilterRestoreUnitIds,
  selectFilterRestoreTypes,
  selectFilterRestoreStatuses,
  selectFilterRestoreFromDate,
  selectFilterRestoreToDate,
  setFilterRestoreUnitIds,
  setFilterRestoreTypes,
  setFilterRestoreStatuses,
  setFilterRestoreFromDate,
  setFilterRestoreToDate,
} from './slice';
import { useNotification } from '../../../components/Notification';

// Schedule type constants (matching API: 0=Event, 1=Daily, 2=Weekly)
const SCHEDULE_TYPE = {
  EVENT: 0,
  DAILY: 1,
  WEEKLY: 2,
};

// Days of week
const DAYS_OF_WEEK = [
  { key: 'monday', label: 'syncData.days.monday' },
  { key: 'tuesday', label: 'syncData.days.tuesday' },
  { key: 'wednesday', label: 'syncData.days.wednesday' },
  { key: 'thursday', label: 'syncData.days.thursday' },
  { key: 'friday', label: 'syncData.days.friday' },
  { key: 'saturday', label: 'syncData.days.saturday' },
  { key: 'sunday', label: 'syncData.days.sunday' },
];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`unitsBackup-tabpanel-${index}`}
      aria-labelledby={`unitsBackup-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function UnitsBackup() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);

  // Unit state from Redux
  const units = useSelector(selectUnits);
  const backupConfig = useSelector(selectBackupConfig);
  const saveLoading = useSelector(selectSaveLoading);
  const saveSuccess = useSelector(selectSaveSuccess);
  const saveSuccessMessage = useSelector(selectSaveSuccessMessage);
  const saveError = useSelector(selectSaveError);
  const manualBackupLoading = useSelector(selectManualBackupLoading);
  const manualBackupSuccess = useSelector(selectManualBackupSuccess);
  const manualBackupSuccessMessage = useSelector(selectManualBackupSuccessMessage);
  const manualBackupError = useSelector(selectManualBackupError);
  // Backup logs state from Redux
  const backupLogs = useSelector(selectBackupLogs);
  const backupLogsLoading = useSelector(selectBackupLogsLoading);
  const backupLogsTotal = useSelector(selectBackupLogsTotal);
  const backupLogsPage = useSelector(selectBackupLogsPage);
  const backupLogsSize = useSelector(selectBackupLogsSize);
  // Mark backup log state
  const markLoading = useSelector(selectMarkLoading);
  const markSuccess = useSelector(selectMarkSuccess);
  const markSuccessMessage = useSelector(selectMarkSuccessMessage);
  const markError = useSelector(selectMarkError);
  // Restore backup log state
  const restoreLoading = useSelector(selectRestoreLoading);
  const restoreSuccess = useSelector(selectRestoreSuccess);
  const restoreSuccessMessage = useSelector(selectRestoreSuccessMessage);
  const restoreError = useSelector(selectRestoreError);
  // Filter options from init data
  const types = useSelector(selectTypes);
  const statuses = useSelector(selectStatuses);
  const marks = useSelector(selectMarks);
  // Filter state
  const filterUnitIds = useSelector(selectFilterUnitIds);
  const filterTypes = useSelector(selectFilterTypes);
  const filterStatuses = useSelector(selectFilterStatuses);
  const filterIsMarks = useSelector(selectFilterIsMarks);
  const filterFromDate = useSelector(selectFilterFromDate);
  const filterToDate = useSelector(selectFilterToDate);
  // Restore logs state (Tab 3)
  const restoreLogs = useSelector(selectRestoreLogs);
  const restoreLogsLoading = useSelector(selectRestoreLogsLoading);
  const restoreLogsTotal = useSelector(selectRestoreLogsTotal);
  const restoreLogsPage = useSelector(selectRestoreLogsPage);
  const restoreLogsSize = useSelector(selectRestoreLogsSize);
  // Restore logs filter state
  const filterRestoreUnitIds = useSelector(selectFilterRestoreUnitIds);
  const filterRestoreTypes = useSelector(selectFilterRestoreTypes);
  const filterRestoreStatuses = useSelector(selectFilterRestoreStatuses);
  const filterRestoreFromDate = useSelector(selectFilterRestoreFromDate);
  const filterRestoreToDate = useSelector(selectFilterRestoreToDate);
  const [selectedUnit, setSelectedUnit] = useState('');
  // Confirm dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);
  // Download state
  const [downloadingIds, setDownloadingIds] = useState([]);
  // Restore from file state
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreFileLoading, setRestoreFileLoading] = useState(false);

  // Backup config state
  const [scheduleType, setScheduleType] = useState(null);
  const [executionTime, setExecutionTime] = useState('');
  const [selectedDay, setSelectedDay] = useState('');

  // Fetch init data on mount
  useEffect(() => {
    dispatch(fetchInitRequest());
  }, [dispatch]);

  // Populate form when backupConfig changes
  useEffect(() => {
    if (backupConfig) {
      setScheduleType(backupConfig.type ?? null);
      setExecutionTime(backupConfig.scheduleTime || '');
      setSelectedDay(backupConfig.scheduleDay?.toLowerCase() || '');
    } else {
      // Clear form when no config found
      setScheduleType(null);
      setExecutionTime('');
      setSelectedDay('');
    }
  }, [backupConfig]);

  // Show notification on save success/error
  useEffect(() => {
    if (saveSuccess) {
      showNotification(saveSuccessMessage || t('syncData.config.saveSuccess'), 'success');
      dispatch(clearSaveStatus());
    }
    if (saveError) {
      showNotification(saveError, 'error');
      dispatch(clearSaveStatus());
    }
  }, [saveSuccess, saveSuccessMessage, saveError, showNotification, dispatch, t]);

  // Show notification on manual backup success/error
  useEffect(() => {
    if (manualBackupSuccess) {
      showNotification(manualBackupSuccessMessage || t('syncData.config.manualBackupSuccess'), 'success');
      dispatch(clearManualBackupStatus());
    }
    if (manualBackupError) {
      showNotification(manualBackupError, 'error');
      dispatch(clearManualBackupStatus());
    }
  }, [manualBackupSuccess, manualBackupSuccessMessage, manualBackupError, showNotification, dispatch, t]);

  // Fetch backup logs when Tab 2 is selected or pagination/filters change
  useEffect(() => {
    if (tabValue === 1) {
      dispatch(fetchBackupLogsRequest({ page: backupLogsPage, size: backupLogsSize }));
    }
  }, [dispatch, tabValue, backupLogsPage, backupLogsSize, filterUnitIds, filterTypes, filterStatuses, filterIsMarks, filterFromDate, filterToDate]);

  // Fetch restore logs when Tab 3 is selected or pagination/filters change
  useEffect(() => {
    if (tabValue === 2) {
      dispatch(fetchRestoreLogsRequest({ page: restoreLogsPage, size: restoreLogsSize }));
    }
  }, [dispatch, tabValue, restoreLogsPage, restoreLogsSize, filterRestoreUnitIds, filterRestoreTypes, filterRestoreStatuses, filterRestoreFromDate, filterRestoreToDate]);

  // Show notification on mark success/error
  useEffect(() => {
    if (markSuccess) {
      showNotification(markSuccessMessage || t('syncData.history.confirmSuccess'), 'success');
      dispatch(clearMarkStatus());
      setConfirmDialogOpen(false);
      setSelectedLogId(null);
    }
    if (markError) {
      showNotification(markError, 'error');
      dispatch(clearMarkStatus());
    }
  }, [markSuccess, markSuccessMessage, markError, showNotification, dispatch, t]);

  // Show notification on restore success/error
  useEffect(() => {
    if (restoreSuccess) {
      showNotification(restoreSuccessMessage || t('syncData.history.restoreSuccess'), 'success');
      dispatch(clearRestoreStatus());
    }
    if (restoreError) {
      showNotification(restoreError, 'error');
      dispatch(clearRestoreStatus());
    }
  }, [restoreSuccess, restoreSuccessMessage, restoreError, showNotification, dispatch, t]);

  // Map units to options format for Select component
  const unitOptions = units.map((unit) => ({
    id: unit.id,
    name: unit.name,
  }));

  // Schedule type options for Select component
  const scheduleTypeOptions = [
    { id: SCHEDULE_TYPE.WEEKLY, name: t('syncData.config.weekly') },
    { id: SCHEDULE_TYPE.DAILY, name: t('syncData.config.daily') },
    { id: SCHEDULE_TYPE.EVENT, name: t('syncData.config.event') },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUnitChange = (event) => {
    const unitId = event.target.value;
    setSelectedUnit(unitId);
    if (unitId) {
      dispatch(fetchBackupConfigRequest(unitId));
    } else {
      dispatch(clearBackupConfig());
    }
  };

  const handleScheduleTypeChange = (event) => {
    setScheduleType(event.target.value);
  };

  const handleExecutionTimeChange = (event) => {
    setExecutionTime(event.target.value);
  };

  const handleDayChange = (event, newDay) => {
    if (newDay !== null) {
      setSelectedDay(newDay);
    }
  };

  const handleSave = () => {
    const configData = {
      type: scheduleType,
      scheduleTime: executionTime || null,
      scheduleDay: selectedDay || null,
      unitId: parseInt(selectedUnit, 10),
    };
    dispatch(saveBackupConfigRequest({ unitId: selectedUnit, configData }));
  };

  const handleManualBackup = () => {
    dispatch(manualBackupRequest(selectedUnit));
  };

  // Backup logs pagination handlers
  const handleLogsPageChange = (newPage) => {
    dispatch(setBackupLogsPage(newPage));
  };

  const handleLogsPageSizeChange = (newSize) => {
    dispatch(setBackupLogsSize(newSize));
  };

  // Filter handlers
  const handleFilterUnitIdsChange = (event) => {
    dispatch(setFilterUnitIds(event.target.value));
  };

  const handleFilterTypesChange = (event) => {
    dispatch(setFilterTypes(event.target.value));
  };

  const handleFilterStatusesChange = (event) => {
    dispatch(setFilterStatuses(event.target.value));
  };

  const handleFilterIsMarksChange = (event) => {
    dispatch(setFilterIsMarks(event.target.value));
  };

  const handleFilterFromDateChange = (event) => {
    dispatch(setFilterFromDate(event.target.value));
  };

  const handleFilterToDateChange = (event) => {
    dispatch(setFilterToDate(event.target.value));
  };

  // Restore logs pagination handlers (Tab 3)
  const handleRestoreLogsPageChange = (newPage) => {
    dispatch(setRestoreLogsPage(newPage));
  };

  const handleRestoreLogsPageSizeChange = (newSize) => {
    dispatch(setRestoreLogsSize(newSize));
  };

  // Restore logs filter handlers (Tab 3)
  const handleFilterRestoreUnitIdsChange = (event) => {
    dispatch(setFilterRestoreUnitIds(event.target.value));
  };

  const handleFilterRestoreTypesChange = (event) => {
    dispatch(setFilterRestoreTypes(event.target.value));
  };

  const handleFilterRestoreStatusesChange = (event) => {
    dispatch(setFilterRestoreStatuses(event.target.value));
  };

  const handleFilterRestoreFromDateChange = (event) => {
    dispatch(setFilterRestoreFromDate(event.target.value));
  };

  const handleFilterRestoreToDateChange = (event) => {
    dispatch(setFilterRestoreToDate(event.target.value));
  };

  // Confirm dialog handlers
  const handleOpenConfirmDialog = (logId) => {
    setSelectedLogId(logId);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedLogId(null);
  };

  const handleConfirmMark = () => {
    if (selectedLogId) {
      dispatch(markBackupLogRequest(selectedLogId));
    }
  };

  // Handle restore backup
  const handleRestore = (id) => {
    dispatch(restoreBackupLogRequest(id));
  };

  // Download handler
  const handleDownload = async (logId) => {
    try {
      setDownloadingIds((prev) => [...prev, logId]);
      await unitsBackupService.downloadBackupFile(logId);
      showNotification(t('syncData.history.downloadSuccess'), 'success');
    } catch (error) {
      showNotification(error.message || t('syncData.history.downloadError'), 'error');
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== logId));
    }
  };

  // Restore from file handlers
  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setRestoreFile(file);
  };

  const handleRestoreFromFile = async () => {
    if (!selectedUnit || !restoreFile) return;
    try {
      setRestoreFileLoading(true);
      await unitsBackupService.restoreFromFile(selectedUnit, restoreFile);
      showNotification(t('syncData.config.restoreFileSuccess'), 'success');
      setRestoreFile(null);
      // Reset file input
      const fileInput = document.getElementById('unitsBackup-restore-file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      showNotification(error.message || t('syncData.config.restoreFileError'), 'error');
    } finally {
      setRestoreFileLoading(false);
    }
  };

  // Render action for table row
  const renderActions = (row) => {
    const isDownloading = downloadingIds.includes(row.id);
    const canDownload = !!row.fileName;

    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {/* Download button - enabled when totalFile > 0 */}
        <Tooltip title={canDownload ? t('syncData.history.actions.download') : t('syncData.history.actions.noFiles')}>
          <span>
            <IconButton
              color="primary"
              onClick={() => handleDownload(row.id)}
              disabled={!canDownload || isDownloading}
              id={`unitsBackup-backupLogs-table-button-download-${row.id}`}
              name={`unitsBackup-backupLogs-table-button-download-${row.id}`}
              data-testid={`unitsBackup-backupLogs-table-button-download-${row.id}`}
            >
              {isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {/* Confirm button - show when isMark is false */}
        {row.isMark === false && (
          <Tooltip title={t('syncData.history.actions.confirm')}>
            <IconButton
              color="primary"
              onClick={() => handleOpenConfirmDialog(row.id)}
              id={`unitsBackup-backupLogs-table-button-confirm-${row.id}`}
              name={`unitsBackup-backupLogs-table-button-confirm-${row.id}`}
              data-testid={`unitsBackup-backupLogs-table-button-confirm-${row.id}`}
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
        )}
        {/* Restore button - always show */}
        <Tooltip title={t('syncData.history.actions.restore')}>
          <IconButton
            color="primary"
            onClick={() => handleRestore(row.id)}
            disabled={restoreLoading}
            id={`unitsBackup-backupLogs-table-button-restore-${row.id}`}
            name={`unitsBackup-backupLogs-table-button-restore-${row.id}`}
            data-testid={`unitsBackup-backupLogs-table-button-restore-${row.id}`}
          >
            <RestoreIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Helper function to get type label (0=Automatic, 1=Manual)
  const getTypeLabel = (type) => {
    switch (type) {
      case 0:
        return t('syncData.history.type.automatic');
      case 1:
        return t('syncData.history.type.manual');
      default:
        return type;
    }
  };

  // Helper function to get status chip (0=Processing, 1=Completed, 2=Error)
  const getStatusChip = (status, message) => {
    const statusConfig = {
      0: { label: t('syncData.history.status.processing'), color: 'info' },
      1: { label: t('syncData.history.status.completed'), color: 'success' },
      2: { label: t('syncData.history.status.error'), color: 'error' },
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    const chip = <Chip label={config.label} color={config.color} size="small" />;

    // Show tooltip with message if message exists
    if (message) {
      return (
        <Tooltip title={message} arrow>
          <span>{chip}</span>
        </Tooltip>
      );
    }
    return chip;
  };

  // Helper function to get restore type label (Tab 3)
  const getRestoreTypeLabel = (type) => {
    switch (type) {
      case 0:
        return t('syncData.restoreHistory.type.automatic');
      case 1:
        return t('syncData.restoreHistory.type.manual');
      default:
        return type;
    }
  };

  // Helper function to get restore status chip (Tab 3)
  const getRestoreStatusChip = (status, message) => {
    const statusConfig = {
      0: { label: t('syncData.restoreHistory.status.processing'), color: 'info' },
      1: { label: t('syncData.restoreHistory.status.completed'), color: 'success' },
      2: { label: t('syncData.restoreHistory.status.error'), color: 'error' },
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    const chip = <Chip label={config.label} color={config.color} size="small" />;

    if (message) {
      return (
        <Tooltip title={message} arrow>
          <span>{chip}</span>
        </Tooltip>
      );
    }
    return chip;
  };

  // Format file size (bytes to human-readable)
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date time
  const formatDateTime = (dateString) => {
    return formatDateTimeWithCulture(dateString, i18n.language) || '-';
  };

  // Backup logs table columns
  const backupLogsColumns = [
    {
      key: 'createdOn',
      label: t('syncData.history.table.createdOn'),
      render: (value) => formatDateTime(value),
    },
    { key: 'unitName', label: t('syncData.history.table.unitName') },
    { key: 'unitCode', label: t('syncData.history.table.unitCode') },
    {
      key: 'type',
      label: t('syncData.history.table.type'),
      render: (value) => getTypeLabel(value),
    },
    {
      key: 'status',
      label: t('syncData.history.table.status'),
      render: (value, row) => getStatusChip(value, row?.message),
    },
    {
      key: 'fileSize',
      label: t('syncData.history.table.fileSize'),
      render: (value) => formatFileSize(value),
    },
    {
      key: 'totalTime',
      label: t('syncData.history.table.totalTime'),
      render: (value) => value ? `${(value / 1000).toFixed(2)} s` : '-',
    },
    {
      key: 'isMark',
      label: t('syncData.history.table.isMark'),
      render: (value) => (
        <Chip
          label={value ? t('syncData.history.isMark.yes') : t('syncData.history.isMark.no')}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
  ];

  // Restore logs table columns (same as backup logs but without isMark column)
  const restoreLogsColumns = [
    {
      key: 'createdOn',
      label: t('syncData.restoreHistory.table.createdOn'),
      render: (value) => formatDateTime(value),
    },
    { key: 'unitName', label: t('syncData.restoreHistory.table.unitName') },
    { key: 'unitCode', label: t('syncData.restoreHistory.table.unitCode') },
    {
      key: 'type',
      label: t('syncData.restoreHistory.table.type'),
      render: (value) => getRestoreTypeLabel(value),
    },
    {
      key: 'status',
      label: t('syncData.restoreHistory.table.status'),
      render: (value, row) => getRestoreStatusChip(value, row?.message),
    },
    {
      key: 'fileSize',
      label: t('syncData.restoreHistory.table.fileSize'),
      render: (value) => formatFileSize(value),
    },
    {
      key: 'totalTime',
      label: t('syncData.restoreHistory.table.totalTime'),
      render: (value) => value ? `${(value / 1000).toFixed(2)} s` : '-',
    },
  ];

  // Determine which fields to show based on schedule type
  // Hide all when scheduleType is null or Event (0)
  const showExecutionTime = scheduleType !== null && scheduleType !== SCHEDULE_TYPE.EVENT;
  const showDaysOfWeek = scheduleType === SCHEDULE_TYPE.WEEKLY;

  // Disable form when no unit is selected
  const isFormDisabled = !selectedUnit;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StorageIcon fontSize="small" />
            <Typography variant="body2">{t('backup.breadcrumb.backup')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('backup.unitsBackup.breadcrumb')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('backup.unitsBackup.title')}
          </Typography>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="units backup tabs"
          >
            <Tab
              icon={<SettingsIcon />}
              iconPosition="start"
              label={t('syncData.tabs.backupConfig')}
              id="unitsBackup-tab-0"
              aria-controls="unitsBackup-tabpanel-0"
              data-testid="unitsBackup-tab-backupConfig"
            />
            <Tab
              icon={<HistoryIcon />}
              iconPosition="start"
              label={t('syncData.tabs.syncHistory')}
              id="unitsBackup-tab-1"
              aria-controls="unitsBackup-tabpanel-1"
              data-testid="unitsBackup-tab-syncHistory"
            />
            <Tab
              icon={<RestoreHistoryIcon />}
              iconPosition="start"
              label={t('syncData.tabs.restoreHistory')}
              id="unitsBackup-tab-2"
              aria-controls="unitsBackup-tabpanel-2"
              data-testid="unitsBackup-tab-restoreHistory"
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
          {/* Tab 1: Backup Config */}
          <TabPanel value={tabValue} index={0}>
            {/* Unit Selection and Manual Backup Button */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box sx={{ minWidth: 300 }}>
                <Select
                  label={t('syncData.config.unit')}
                  value={selectedUnit}
                  onChange={handleUnitChange}
                  options={unitOptions}
                  required
                  id="unit"
                  name="unit"
                  data-testid="unitsBackup-select-unit"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={manualBackupLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                onClick={handleManualBackup}
                disabled={!selectedUnit || manualBackupLoading}
                id="manualBackup"
                name="manualBackup"
                data-testid="unitsBackup-button-manualBackup"
              >
                {t('syncData.config.manualBackup')}
              </Button>
            </Box>

            <Paper
              elevation={1}
              sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                opacity: isFormDisabled ? 0.6 : 1,
                pointerEvents: isFormDisabled ? 'none' : 'auto',
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'left' }}>
                {t('syncData.config.title')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3, justifyContent: 'flex-start' }}>
                {/* Schedule Type */}
                <Box sx={{ minWidth: 300 }}>
                  <Select
                    label={t('syncData.config.scheduleType')}
                    value={scheduleType}
                    onChange={handleScheduleTypeChange}
                    options={scheduleTypeOptions}
                    required
                    disabled={isFormDisabled}
                    id="scheduleType"
                    name="scheduleType"
                    data-testid="unitsBackup-select-scheduleType"
                  />
                </Box>

                {/* Execution Time - Hidden for Event type */}
                {showExecutionTime && (
                  <Box sx={{ minWidth: 200 }}>
                    <TimePicker
                      label={t('syncData.config.executionTime')}
                      value={executionTime}
                      onChange={handleExecutionTimeChange}
                      required
                      disabled={isFormDisabled}
                      id="executionTime"
                      name="executionTime"
                      data-testid="unitsBackup-input-executionTime"
                    />
                  </Box>
                )}
              </Box>

              {/* Days of Week - Only shown for Weekly type */}
              {showDaysOfWeek && (
                <Box sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1.5, color: 'text.secondary' }}
                  >
                    {t('syncData.config.daysOfWeek')} <span style={{ color: '#d32f2f' }}>*</span>
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedDay}
                    exclusive
                    onChange={handleDayChange}
                    disabled={isFormDisabled}
                    aria-label="day of week"
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      justifyContent: 'flex-start',
                    }}
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <ToggleButton
                        key={day.key}
                        value={day.key}
                        id={`day-${day.key}`}
                        name={`day-${day.key}`}
                        data-testid={`unitsBackup-toggle-day-${day.key}`}
                        sx={{
                          px: 2,
                          py: 1,
                          border: '1px solid #e0e0e0',
                          '&.Mui-selected': {
                            backgroundColor: '#2d8659',
                            color: '#ffffff',
                            borderColor: '#2d8659',
                            '&:hover': {
                              backgroundColor: '#238b4e',
                            },
                          },
                        }}
                      >
                        {t(day.label)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saveLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={isFormDisabled || saveLoading}
                  id="saveConfig"
                  name="saveConfig"
                  data-testid="unitsBackup-button-saveConfig"
                >
                  {t('syncData.config.save')}
                </Button>
              </Box>
            </Paper>

            {/* Restore from File Form */}
            <Paper
              elevation={1}
              sx={{
                p: 3,
                mt: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                opacity: isFormDisabled ? 0.6 : 1,
                pointerEvents: isFormDisabled ? 'none' : 'auto',
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'left' }}>
                {t('syncData.config.restoreTitle')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={isFormDisabled}
                  id="selectFile"
                  name="selectFile"
                  data-testid="unitsBackup-button-selectFile"
                >
                  {t('syncData.config.selectFile')}
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".zip,.bak,.sql"
                    id="unitsBackup-restore-file-input"
                    name="unitsBackup-restore-file-input"
                    data-testid="unitsBackup-input-restoreFile"
                  />
                </Button>
                {restoreFile && (
                  <Typography variant="body2" color="text.secondary">
                    {restoreFile.name}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={restoreFileLoading ? <CircularProgress size={20} color="inherit" /> : <RestoreIcon />}
                  onClick={handleRestoreFromFile}
                  disabled={isFormDisabled || !restoreFile || restoreFileLoading}
                  id="restoreFromFile"
                  name="restoreFromFile"
                  data-testid="unitsBackup-button-restoreFromFile"
                >
                  {t('syncData.config.restoreData')}
                </Button>
              </Box>
            </Paper>
          </TabPanel>

          {/* Tab 2: Sync History */}
          <TabPanel value={tabValue} index={1}>
            {/* Filter Section */}
            <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.history.filter.units')}
                    value={filterUnitIds}
                    onChange={handleFilterUnitIdsChange}
                    options={units}
                    placeholder={t('syncData.history.filter.unitsPlaceholder')}
                    id="filterUnitIds"
                    name="filterUnitIds"
                    data-testid="unitsBackup-select-filterUnitIds"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.history.filter.types')}
                    value={filterTypes}
                    onChange={handleFilterTypesChange}
                    options={types}
                    placeholder={t('syncData.history.filter.typesPlaceholder')}
                    id="filterTypes"
                    name="filterTypes"
                    data-testid="unitsBackup-select-filterTypes"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.history.filter.statuses')}
                    value={filterStatuses}
                    onChange={handleFilterStatusesChange}
                    options={statuses}
                    placeholder={t('syncData.history.filter.statusesPlaceholder')}
                    id="filterStatuses"
                    name="filterStatuses"
                    data-testid="unitsBackup-select-filterStatuses"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.history.filter.marks')}
                    value={filterIsMarks}
                    onChange={handleFilterIsMarksChange}
                    options={marks}
                    placeholder={t('syncData.history.filter.marksPlaceholder')}
                    id="filterIsMarks"
                    name="filterIsMarks"
                    data-testid="unitsBackup-select-filterIsMarks"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DateRangePicker
                    label={t('syncData.history.filter.dateRange')}
                    fromValue={filterFromDate}
                    toValue={filterToDate}
                    onFromChange={handleFilterFromDateChange}
                    onToChange={handleFilterToDateChange}
                    id="filterDateRange"
                    name="filterDateRange"
                    data-testid="unitsBackup-daterangepicker-filter"
                  />
                </Grid>
              </Grid>
            </Box>
            <Table
              id="unitsBackup-backupLogs-table"
              columns={backupLogsColumns}
              data={backupLogs}
              loading={backupLogsLoading}
              pagination={true}
              page={backupLogsPage}
              pageSize={backupLogsSize}
              totalItems={backupLogsTotal}
              onPageChange={handleLogsPageChange}
              onPageSizeChange={handleLogsPageSizeChange}
              emptyMessage={t('syncData.history.noData')}
              renderActions={renderActions}
            />
          </TabPanel>

          {/* Tab 3: Restore History */}
          <TabPanel value={tabValue} index={2}>
            {/* Filter Section */}
            <Box sx={{ pb: 2, mb: 2, borderBottom: '1px solid #e5e7eb' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.restoreHistory.filter.units')}
                    value={filterRestoreUnitIds}
                    onChange={handleFilterRestoreUnitIdsChange}
                    options={units}
                    placeholder={t('syncData.restoreHistory.filter.unitsPlaceholder')}
                    id="filterRestoreUnitIds"
                    name="filterRestoreUnitIds"
                    data-testid="unitsBackup-select-filterRestoreUnitIds"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.restoreHistory.filter.types')}
                    value={filterRestoreTypes}
                    onChange={handleFilterRestoreTypesChange}
                    options={types}
                    placeholder={t('syncData.restoreHistory.filter.typesPlaceholder')}
                    id="filterRestoreTypes"
                    name="filterRestoreTypes"
                    data-testid="unitsBackup-select-filterRestoreTypes"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MultiSelect
                    label={t('syncData.restoreHistory.filter.statuses')}
                    value={filterRestoreStatuses}
                    onChange={handleFilterRestoreStatusesChange}
                    options={statuses}
                    placeholder={t('syncData.restoreHistory.filter.statusesPlaceholder')}
                    id="filterRestoreStatuses"
                    name="filterRestoreStatuses"
                    data-testid="unitsBackup-select-filterRestoreStatuses"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DateRangePicker
                    label={t('syncData.restoreHistory.filter.dateRange')}
                    fromValue={filterRestoreFromDate}
                    toValue={filterRestoreToDate}
                    onFromChange={handleFilterRestoreFromDateChange}
                    onToChange={handleFilterRestoreToDateChange}
                    id="filterRestoreDateRange"
                    name="filterRestoreDateRange"
                    data-testid="unitsBackup-daterangepicker-filterRestore"
                  />
                </Grid>
              </Grid>
            </Box>
            <Table
              id="unitsBackup-restoreLogs-table"
              columns={restoreLogsColumns}
              data={restoreLogs}
              loading={restoreLogsLoading}
              pagination={true}
              page={restoreLogsPage}
              pageSize={restoreLogsSize}
              totalItems={restoreLogsTotal}
              onPageChange={handleRestoreLogsPageChange}
              onPageSizeChange={handleRestoreLogsPageSizeChange}
              emptyMessage={t('syncData.restoreHistory.noData')}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Confirm Mark Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {t('syncData.history.confirmDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {t('syncData.history.confirmDialog.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirmDialog}
            id="confirmDialog-cancel"
            name="confirmDialog-cancel"
            data-testid="unitsBackup-confirmDialog-button-cancel"
          >
            {t('syncData.history.confirmDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmMark}
            variant="contained"
            color="primary"
            disabled={markLoading}
            startIcon={markLoading ? <CircularProgress size={20} color="inherit" /> : null}
            id="confirmDialog-confirm"
            name="confirmDialog-confirm"
            data-testid="unitsBackup-confirmDialog-button-confirm"
          >
            {t('syncData.history.confirmDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UnitsBackup;
